<!--
  AcceptInvitation — magic-link landing for the Client Store Owner
  invitation flow (ADR 0050 / Phase F5).

  The recipient arrives at /auth/accept-invitation/:token from the
  invitation email. We:

    1. Pre-flight GET /api/auth/accept-invitation/:token to verify the
       token is still valid + fetch the recipient's email and the
       Client / Distributor names so the page can render specific
       copy.
    2. Render a set-password form (the shared account-password policy,
       utils/passwordPolicy.js) with an
       optional phone number so the distributor can reach the client.
    3. POST to the same URL with {password, phone?} -> bearer token + redirect.
    4. Persist the session and route to /portal/welcome.

  Copy is matter-of-fact, customer-facing. NO demo / test framing.

  Branding (GH #4425): this is the first page an invited client sees, so it
  carries the same ThreadFlow mark and card treatment as /login and the
  password-reset page, decided by the same pre-auth resolver
  (utils/preAuthBranding.js): branded on the canonical host, 100% generic on
  a configured white-label host (no mark and no "ThreadFlow" in the copy).
-->
<template>
  <div class="accept-invitation-page" data-testid="accept-invitation-page">
    <div class="accept-invitation-card">
      <img
        v-if="showBrand"
        src="@/assets/logo/logo.png"
        alt="ThreadFlow"
        class="accept-invitation-brand"
        data-testid="threadflow-brand-mark"
      />
      <header class="accept-invitation-header">
        <h1 class="accept-invitation-title">Set up your account</h1>
        <p v-if="distributorName" class="accept-invitation-sub">
          {{ distributorName }} invited you to manage
          <strong>{{ clientCompany || 'your store' }}</strong><template v-if="showBrand"> on ThreadFlow</template>.
        </p>
        <p v-else class="accept-invitation-sub">
          You've been invited to manage
          <strong>{{ clientCompany || 'your store' }}</strong><template v-if="showBrand"> on ThreadFlow</template>.
        </p>
      </header>

      <div
        v-if="loading"
        class="accept-invitation-loading"
        data-testid="accept-invitation-loading"
      >
        Checking your invitation...
      </div>

      <div
        v-else-if="tokenError"
        class="accept-invitation-error"
        data-testid="accept-invitation-token-error"
      >
        <p>{{ tokenError }}</p>
        <p class="accept-invitation-help">
          Ask the distributor who invited you to send a new invitation.
        </p>
      </div>

      <form
        v-else
        class="accept-invitation-form"
        @submit.prevent="submit"
        data-testid="accept-invitation-form"
      >
        <label class="accept-invitation-label" for="invited-email">
          Email
          <input
            id="invited-email"
            class="accept-invitation-input"
            :value="invitedEmail"
            type="email"
            readonly
            data-testid="accept-invitation-email"
          />
        </label>

        <label class="accept-invitation-label" for="invited-password">
          Choose a password
          <input
            id="invited-password"
            v-model="password"
            class="accept-invitation-input"
            type="password"
            autocomplete="new-password"
            required
            data-testid="accept-invitation-password"
          />
          <span
            v-if="passwordProblem"
            class="accept-invitation-error-text"
            data-testid="accept-invitation-password-error"
          >
            {{ passwordProblem }}
          </span>
          <span v-else class="accept-invitation-hint">
            At least 8 characters, with a number, an upper-case letter, or a
            symbol.
          </span>
        </label>

        <label class="accept-invitation-label" for="invited-confirm">
          Confirm password
          <input
            id="invited-confirm"
            v-model="confirm"
            class="accept-invitation-input"
            type="password"
            autocomplete="new-password"
            required
            data-testid="accept-invitation-confirm"
          />
        </label>

        <label class="accept-invitation-label" for="invited-phone">
          Phone number
          <input
            id="invited-phone"
            v-model="phone"
            class="accept-invitation-input"
            type="tel"
            autocomplete="tel"
            data-testid="accept-invitation-phone"
          />
          <span class="accept-invitation-hint">
            So your distributor can reach you about orders.
          </span>
        </label>

        <p
          v-if="formError"
          class="accept-invitation-error-text"
          data-testid="accept-invitation-error"
        >
          {{ formError }}
        </p>

        <button
          type="submit"
          class="accept-invitation-submit"
          data-testid="accept-invitation-submit"
          :disabled="!canSubmit || submitting"
        >
          {{ submitting ? 'Setting up...' : 'Create account' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import api from '@/services/api'
import { resolvePreAuthBranding } from '@/utils/preAuthBranding'
import { isValidPhone, PHONE_INVALID_MESSAGE } from '@/utils/phone'
import { validatePassword } from '@/utils/passwordPolicy'


export default {
  name: 'AcceptInvitation',
  data() {
    return {
      showBrand: resolvePreAuthBranding().showBrand,
      loading: true,
      tokenError: '',
      invitedEmail: '',
      invitedFirstName: '',
      invitedLastName: '',
      clientCompany: '',
      distributorName: '',
      password: '',
      confirm: '',
      phone: '',
      submitting: false,
      formError: '',
    }
  },
  computed: {
    token() {
      return this.$route.params.token
    },
    // The rule message while the user has typed something that does not
    // satisfy the shared account-password policy; '' otherwise (and while
    // the field is still empty, so the form does not open in an error).
    passwordProblem() {
      if (!this.password) return ''
      const result = validatePassword(this.password)
      return result === true ? '' : result
    },
    canSubmit() {
      return (
        validatePassword(this.password) === true &&
        this.password === this.confirm
      )
    },
  },
  async mounted() {
    if (!this.token) {
      this.loading = false
      this.tokenError = 'Invitation link is missing a token.'
      return
    }
    try {
      const resp = await api.get(`/auth/accept-invitation/${this.token}`)
      this.invitedEmail = resp.data.invited_email || ''
      this.invitedFirstName = resp.data.invited_first_name || ''
      this.invitedLastName = resp.data.invited_last_name || ''
      this.clientCompany = resp.data.client_name || ''
      this.distributorName = resp.data.distributor_name || ''
    } catch (err) {
      const status = err?.response?.status
      const message = err?.response?.data?.message
      if (status === 404) {
        this.tokenError = 'This invitation link is not recognized.'
      } else if (status === 410) {
        this.tokenError =
          message ||
          'This invitation has expired or has already been used.'
      } else {
        this.tokenError =
          'We could not check this invitation. Please try again later.'
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    async submit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.formError = ''
      if (this.password !== this.confirm) {
        this.formError = 'Passwords do not match.'
        this.submitting = false
        return
      }
      if (!isValidPhone(this.phone)) {
        this.formError = PHONE_INVALID_MESSAGE
        this.submitting = false
        return
      }
      const payload = { password: this.password }
      const phone = String(this.phone || '').trim()
      if (phone) payload.phone = phone
      try {
        const resp = await api.post(
          `/auth/accept-invitation/${this.token}`,
          payload,
        )
        const cleanToken = String(resp.data.token).replace(
          /^["']+|["']+$/g,
          '',
        )
        localStorage.setItem('token', cleanToken)
        localStorage.setItem('userId', resp.data.account_id)
        if (localStorage.getItem('token') !== cleanToken) {
          throw new Error('Token storage failed')
        }
        try {
          const account = await this.$store.dispatch('jv/get', [
            `accounts/${resp.data.account_id}`,
            { params: { include: 'role' } },
          ])
          // GH #2941 defect 2: accepting an invitation establishes a
          // personal session, so clear any anonymous-storefront marker.
          // See router/anonymousStorefrontSession.js.
          await this.$store.dispatch('login', {
            account,
            authResponse: resp.data,
            credentialed: true,
          })
        } catch (e) {
          // Non-fatal; the welcome page will rehydrate via the token.
          // We still navigate to /portal/welcome so the recipient gets
          // a finished state rather than a stuck spinner.
          // eslint-disable-next-line no-console
          console.warn('post-accept hydrate failed', describeError(e))
        }
        const target = resp.data.redirect_to || '/portal/welcome'
        this.$router.push(target)
      } catch (err) {
        const status = err?.response?.status
        const detail =
          err?.response?.data?.message ||
          err?.response?.data?.description
        if (status === 410) {
          this.tokenError =
            detail ||
            'This invitation has expired or has already been used.'
        } else if (status === 409) {
          this.formError =
            detail ||
            'An account already exists for this email. Sign in instead, or ask your distributor to resend the invite.'
        } else if ((status === 401 || status === 403) && detail) {
          // A login gate refused (same message the sign-in page shows).
          this.formError = detail
        } else if (status === 400) {
          this.formError =
            detail || 'Please check your password and phone number and try again.'
        } else {
          this.formError = 'Something went wrong. Please try again.'
        }
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.accept-invitation-page {
  min-height: 100vh;
  /* Same backdrop as /login and password reset. */
  background: #f5f8fa;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 16px;
}
.accept-invitation-card {
  background: var(--tf-bg, #fff);
  max-width: 480px;
  width: 100%;
  padding: 40px 36px;
  border-radius: var(--tf-radius-xl, 36px);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}
.accept-invitation-brand {
  display: block;
  max-height: 120px;
  max-width: 200px;
  margin: 0 auto 24px;
  image-rendering: crisp-edges;
  user-select: none;
}
.accept-invitation-header {
  margin-bottom: 24px;
}
.accept-invitation-title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
  color: var(--tf-fg-1, #111);
}
.accept-invitation-sub {
  margin: 0;
  color: var(--tf-fg-2, #555);
  font-size: 14px;
}
.accept-invitation-loading,
.accept-invitation-error {
  padding: 24px 0;
  color: var(--tf-fg-2, #555);
}
.accept-invitation-error {
  color: #6b3a3a;
}
.accept-invitation-help {
  font-size: 13px;
  color: var(--tf-fg-3, #777);
  margin-top: 8px;
}
.accept-invitation-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.accept-invitation-label {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: var(--tf-fg-2, #555);
  gap: 4px;
}
.accept-invitation-input {
  padding: 10px 12px;
  border: 1px solid var(--tf-border, #d0d4dc);
  border-radius: var(--tf-radius-xs, 4px);
  font-size: 14px;
  background: var(--tf-bg, #fff);
  color: var(--tf-fg-1, #111);
}
.accept-invitation-input[readonly] {
  background: var(--tf-surface-alt, #f6f7fb);
}
.accept-invitation-hint {
  font-size: 12px;
  color: var(--tf-fg-3, #777);
}
.accept-invitation-error-text {
  color: #c0392b;
  font-size: 13px;
  margin: 0;
}
.accept-invitation-submit {
  margin-top: 8px;
  padding: 12px;
  border: none;
  border-radius: var(--tf-radius-pill, 9999px);
  background: var(--tf-primary, #119ae9);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.accept-invitation-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
